/**
 * Conversation resets shared by the entry points that start a new chat.
 *
 * `startNewConversation` clears the session stores, the live run and the composer in one place so the
 * history drawer, the Sessions sidebar and the route cannot drift apart. Opening an EXISTING
 * conversation is `laneV2/openV2Session.ts`.
 */

import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useWeaveSessionStore } from "@/stores/weaveSessionStore";
import { useRunStateStore } from "@/stores/runStateStore";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { useComposerResetStore } from "@/stores/composerResetStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

/**
 * Start a new, empty conversation.
 *
 * ── THE THIRD INSTANCE OF THE SAME DEFECT ────────────────────────────────────────────────────
 *
 * "New session" existed in two places — the history drawer and the Sessions sidebar — and both
 * cleared only the LEGACY store: `clearThread(); reset()`. On the v1 surface that leaves
 * `weaveSessionStore` still naming the conversation the reader just left, so `activeConversationId`
 * keeps reporting it and the first message of the supposedly new chat is filed into the old one.
 * The chat looks empty and is not.
 *
 * Clearing the v1 store is what actually makes it new: no session, no current run, no archive. The
 * legacy clears stay until that surface is deleted.
 */
export function startNewConversation(): void {
  useWeaveSessionStore.getState().clear();
  /*
   * ── AND THE LIVE RUN, OR "NEW SESSION" LEAVES THE OLD ANSWER ON SCREEN ───────────────────────
   *
   * REPORTED AS: creating a new session stopped working after `/weave` moved to the protocol
   * architecture.
   *
   * It used to work by accident of which store the screen read. The legacy transcript rendered
   * `multiAgentStore.thread`, so clearing that store emptied the chat. The transcript now renders
   * the LIVE RUN from `runStateStore`, and nothing here cleared it — so the previous conversation's
   * answer stayed exactly where it was while the session identity underneath it was replaced. The
   * next question then appeared beneath somebody else's answer in a chat claiming to be new.
   *
   * `reset()` returns the protocol store to its initial state — no run, no messages, no plan — which
   * is what an empty conversation is. The stream detaches on its own: `selectRunStreams` reads the
   * run id from `weaveSessionStore.current`, which the line above just cleared.
   */
  useRunStateStore.getState().reset();
  const legacy = useMultiAgentStore.getState();
  legacy.clearThread();
  legacy.reset();
  /*
   * And the lane-v2 view. Without this a pinned v2 session would be re-attached the moment the
   * active conversation went to null (it is the persisted fallback) — "New session" would reopen
   * the old one. `useLaneV2Route` then drafts a fresh v2 conversation if the server offers lane v2.
   */
  useLaneV2SessionStore.getState().reset();
  clearComposerInputs();
}

/**
 * The files and the context chip in the composer belong to the conversation they were added in. Left in place they rode
 * every send of the next conversation (the same file was re-sent by ~45 turns across ~30 new sessions).
 */
export function clearComposerInputs(): void {
  useComposerResetStore.getState().bump();
  useMultiAgentIdeStore.getState().setPendingRefactorFile(null);
}
